/** Расходы касс CRM: ответы ручек `/api/kassa-expenses`, `/api/kassa-sync`, `/api/crm-articles`, `/api/kassas`. */
import { apiFetch, query } from "@/lib/api";
import type { EntrepreneurIds, Money } from "@/lib/pnl";

/** Наличные — деньги мимо банка; документ — безнал, деньги уйдут операцией банка; внутренняя — перенос. */
export type KassaKind = "cash" | "document" | "internal";

export const KASSA_KIND_LABEL: Record<KassaKind, string> = {
  cash: "наличные",
  document: "безнал",
  internal: "внутренняя",
};

export type Kassa = { id: number; crm_id: number; name: string; kind: KassaKind; kind_name: string };

export type KassaComment = { text: string; date: string };

/** Наш комментарий к выплате (не CRM): куда разнести, что выяснили. */
export type KassaNote = { id: number; text: string; user_name: string; date: string };

export type KassaInvoice = {
  crm_id: number;
  amount: Money;
  paid: Money;
  status: string;
  category: string;
  comment: string;
  comments: KassaComment[];
};

export type KassaExpense = {
  id: number;
  crm_id: number;
  date: string;
  date_accounting: string | null;
  /** Со знаком: + выдали из кассы, − вернули в кассу. */
  amount: Money;
  kassa_id: number;
  kassa_name: string;
  kassa_kind: KassaKind;
  location_name: string | null;
  entrepreneur_name: string | null;
  type: string;
  comment: string;
  card_mask: string | null;
  recipient_bank: string | null;
  /** Сотрудник-получатель: имя и ТЕКУЩАЯ роль в CRM. */
  user_crm_id: number | null;
  user_name: string | null;
  user_role: string | null;
  /** Кому ушли деньги, когда это не сотрудник: поставщик, владелец платья, клиент, ФИО по карте. */
  recipient: string | null;
  crm_article_id: number | null;
  crm_article_name: string | null;
  article_name: string | null;
  /** Откуда статья: статья CRM, тип расхода CRM или слова комментария; null — не разнесена. */
  article_source: "crm" | "type" | "comment" | null;
  invoice: KassaInvoice | null;
  comments: KassaComment[];
  /** Наши комментарии — пишутся здесь, в CRM их нет. */
  notes: KassaNote[];
  date_deleted: string | null;
};

export type KassaExpensePage = { total: number; sum: Money; rows: KassaExpense[] };

export type KassaFilter = {
  kassa_id?: number | undefined;
  kind?: KassaKind | undefined;
  entrepreneur_id?: EntrepreneurIds | undefined;
  crm_article_id?: number | undefined;
  article_id?: number | undefined;
  /** Разрезы экрана «Зарплата»: салон выплаты (id города), сотрудник (CRM-id), роль. */
  location_id?: number | undefined;
  user_crm_id?: number | undefined;
  role?: string | undefined;
  search?: string | undefined;
  date_from?: string | undefined;
  date_to?: string | undefined;
  deleted?: boolean | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
};

export type KassaSync = {
  id: number;
  date_start: string;
  date_finish: string | null;
  result: {
    since?: string;
    expenses?: number;
    expenses_new?: number;
    expenses_gone?: number;
    invoices?: number;
    users?: number;
    comments?: number;
    payments?: number;
    payments_new?: number;
    payments_gone?: number;
    checks?: number;
    checks_gone?: number;
  };
  error: string | null;
};

export type CrmArticle = {
  id: number;
  crm_id: number;
  name: string;
  group1: string;
  archived: boolean;
  article_id: number | null;
  article_name: string | null;
  manual: boolean;
  expenses: number;
  sum: Money;
};

/** Оплата наличными в кассу — выручка. */
export type KassaPayment = {
  id: number;
  crm_id: number;
  date: string;
  amount: Money;
  kassa_id: number;
  kassa_name: string;
  location_name: string | null;
  entrepreneur_name: string | null;
  category: string;
  deal_crm_id: number | null;
  date_deleted: string | null;
};

export type KassaPaymentPage = { total: number; sum: Money; rows: KassaPayment[] };

export type KassaPaymentFilter = {
  kassa_id?: number | undefined;
  entrepreneur_id?: EntrepreneurIds | undefined;
  date_from?: string | undefined;
  date_to?: string | undefined;
  deleted?: boolean | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
};

/** Сверка кассы: пересчитали руками; `expected` — сколько должно было быть
 *  (прошлый пересчёт + оплаты − выплаты), `diff` — минус — недостача.
 *  `null` — прошлого пересчёта нет, сравнивать не с чем. */
export type CashCheck = {
  crm_id: number;
  date: string;
  amount: Money;
  expected: Money | null;
  diff: Money | null;
  comment: string;
};

/** День кассы. Остаток `null` — касса ещё ни разу не пересчитана. */
export type CashDay = {
  date: string;
  opening: Money | null;
  income: Money;
  /** Выплаты со знаком: минус — деньги вернули в кассу. */
  expense: Money;
  closing: Money | null;
  /** Последний пересчёт дня; `null` — в этот день не пересчитывали. */
  counted: Money | null;
  /** Сумма расхождений сверок дня: опечатка и её исправление гасятся. */
  diff: Money;
  checks: CashCheck[];
};

export type CashKassa = {
  kassa_id: number;
  name: string;
  opening: Money | null;
  income: Money;
  expense: Money;
  closing: Money | null;
  diff: Money;
  checks: number;
  last_check: { crm_id: number; date: string; amount: Money } | null;
  days: CashDay[];
};

export type CashReport = {
  date_from: string;
  date_to: string;
  kassas: CashKassa[];
  total: { income: Money; expense: Money; diff: Money; closing: Money };
};

export function loadCash(filter: { date_from: string; date_to: string; kassa_id?: number | undefined }): Promise<CashReport> {
  return apiFetch<CashReport>(`/api/kassa-cash${query(filter)}`);
}

export function loadKassaPayments(filter: KassaPaymentFilter): Promise<KassaPaymentPage> {
  return apiFetch<KassaPaymentPage>(`/api/kassa-payments${query(filter)}`);
}

export function loadKassas(): Promise<Kassa[]> {
  return apiFetch<Kassa[]>("/api/kassas");
}

export function loadKassaExpenses(filter: KassaFilter): Promise<KassaExpensePage> {
  return apiFetch<KassaExpensePage>(
    `/api/kassa-expenses${query({ ...filter, deleted: filter.deleted ? "true" : undefined })}`,
  );
}

/** Наш комментарий к выплате; ответ — вся лента её комментариев. */
export function addKassaNote(id: number, text: string): Promise<KassaNote[]> {
  return apiFetch<KassaNote[]>(`/api/kassa-expenses/${id}/notes`, { method: "POST", body: JSON.stringify({ text }) });
}

export function loadLastSync(): Promise<KassaSync | null> {
  return apiFetch<KassaSync | null>("/api/kassa-sync");
}

export function runSync(): Promise<KassaSync["result"] & { id: number }> {
  return apiFetch("/api/kassa-sync", { method: "POST" });
}

export function loadCrmArticles(): Promise<CrmArticle[]> {
  return apiFetch<CrmArticle[]>("/api/crm-articles");
}

/** `null` — вернуть автосопоставление. */
export function setCrmArticle(id: number, article_id: number | null): Promise<CrmArticle> {
  return apiFetch<CrmArticle>(`/api/crm-articles/${id}`, { method: "PATCH", body: JSON.stringify({ article_id }) });
}
